import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Users, TrendingUp, Sparkles, Lightbulb, MessageCircle, Inbox, ShieldCheck, Droplets, Trophy, Check } from "lucide-react";
import { api } from "@/lib/api";
import { HandleAvatar } from "@/components/HandleAvatar";
import { CATEGORY_META, LEVEL_COLORS } from "@/components/community/meta";
import { SampleTag } from "@/components/community/SampleTag";

export function CategoryNav({ categories, activeCat, onSelect }) {
  return (
    <div className="lg:col-span-4 space-y-2">
      <div className="flex items-center gap-2 text-stone-800 mb-1">
        <Users className="w-4 h-4" />
        <span className="text-xs font-bold uppercase tracking-widest">Explore categories</span>
      </div>
      {categories.map((c) => {
        const meta = CATEGORY_META[c.category];
        return (
          <button key={c.category} data-testid={`category-item-${c.category}`} onClick={() => onSelect(c.category)}
            className={`w-full text-left warm-card p-4 flex items-center justify-between transition ${activeCat === c.category ? "ring-2 ring-amber-600 border-amber-300" : ""}`}>
            <div>
              <div className="font-semibold text-stone-900 flex items-center gap-2"><span className="text-lg">{meta.emoji}</span> {meta.label}</div>
              <div className="text-xs text-stone-500 mt-0.5">{c.members} member{c.members === 1 ? "" : "s"}</div>
            </div>
            <TrendingUp className="w-4 h-4 text-stone-400" />
          </button>
        );
      })}
    </div>
  );
}

export function CategoryHero({ category }) {
  const meta = CATEGORY_META[category];
  return (
    <motion.div key={category} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className={`rounded-3xl p-6 bg-gradient-to-br ${meta.tint} border border-stone-200/60 mb-4`}>
      <div className="flex items-center gap-3">
        <span className="text-3xl">{meta.emoji}</span>
        <div>
          <h2 className="font-display text-2xl font-bold text-stone-900">{meta.label}</h2>
          <p className="text-sm text-stone-600">Leaderboard by points level. Private habits, shared spirit — water someone's garden (+1 for you both) or leave a cheer.</p>
        </div>
      </div>
    </motion.div>
  );
}

const rankCls = (rank) => rank === 1 ? "bg-amber-400 text-stone-900" : rank === 2 ? "bg-stone-300 text-stone-800" : rank === 3 ? "bg-orange-200 text-orange-900" : "bg-stone-100 text-stone-500";

function WaterButton({ m, onWater, gate }) {
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState({ done: m.watered_by_me, n: m.waters_today || 0 });
  const [offer, setOffer] = useState(false);
  const water = async () => {
    if (gate.sharingOff) return gate.onNeedSharing();
    if (gate.joinCat && !offer) return setOffer(true);
    setBusy(true);
    try {
      const r = await api.post(`/community/members/${m.id}/water`, null, { params: gate.joinCat ? { join: gate.joinCat } : {} });
      if (gate.joinCat) { toast.success(`You joined ${gate.catLabel}.`); gate.onJoined?.(); }
      setState({ done: true, n: r.data.waters_today });
      toast.success(`You watered ${m.handle}'s garden — +1 for them, +1 for you.`);
      if (r.data.achievement) toast.success(`Achievement unlocked: ${r.data.achievement.title} (+${r.data.achievement.points} pts)`);
      onWater?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't water just now."); }
    finally { setBusy(false); }
  };
  return (
    <button data-testid={`water-${m.id}`} onClick={water} disabled={busy || state.done} title={gate.sharingOff ? "Turn on sharing to water gardens" : state.done ? "Watered today" : "Water their garden (+1 each)"}
      className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full transition ${gate.sharingOff ? "bg-stone-100 text-stone-400" : state.done ? "bg-sky-100 text-sky-800" : "bg-sky-50 text-sky-800 hover:bg-sky-600 hover:text-white"} disabled:cursor-default`}>
      <Droplets className="w-3.5 h-3.5" /> {offer && !state.done ? `Join ${gate.catLabel} & water` : state.done ? "Watered" : "Water"}{state.n > 0 && <span className="font-mono opacity-80" data-testid={`waters-${m.id}`}>· {state.n}</span>}
    </button>
  );
}

/** Slim bar for a member's progress toward this week's category challenge. */
function MemberChallenge({ c, id }) {
  if (!c) return null;
  if (!c.joined) return <div className="text-[10px] text-stone-400 mt-1.5" data-testid={`member-challenge-${id}`}>Not in this week's challenge</div>;
  const pct = Math.min(100, Math.round((c.progress / c.target) * 100));
  return (
    <div className="flex items-center gap-2 mt-1.5" data-testid={`member-challenge-${id}`} data-progress={c.progress} data-target={c.target}>
      <Trophy className={`w-3 h-3 ${c.completed ? "text-emerald-700" : "text-amber-700"}`} />
      <div className="h-1.5 w-28 rounded-full bg-stone-100 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${c.completed ? "bg-emerald-600" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] font-mono text-stone-600">{c.completed ? <span className="inline-flex items-center gap-0.5 text-emerald-700 font-bold"><Check className="w-3 h-3" /> done</span> : `${c.progress}/${c.target}`}</span>
    </div>
  );
}

function MemberRow({ m, index, gate, onOpenWall, onWatered }) {
  const openWall = () => (gate.sharingOff && !m.is_me ? gate.onNeedSharing() : onOpenWall(m));
  return (
    <motion.li initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.03 }}
      data-testid={`member-${m.id}`} className={`warm-card p-4 flex items-center justify-between ${m.is_me ? "ring-2 ring-emerald-500" : ""}`}>
      <div className="flex items-center gap-3">
        <div data-testid={`rank-${m.id}`} className={`w-7 h-7 rounded-full flex items-center justify-center font-mono text-xs font-bold ${rankCls(m.rank)}`}>{m.rank}</div>
        <HandleAvatar handle={m.handle} />
        <div>
          <div className="font-semibold text-stone-900 flex items-center gap-2">
            <span data-testid={`member-handle-${m.id}`}>{m.handle}</span> {m.is_me && <span className="text-xs text-emerald-700">(you)</span>}
            {m.is_sample && <SampleTag id={m.id} />}
            {m.accept_suggestions && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-amber-800" title="Open to suggestions"><Lightbulb className="w-3 h-3" /> open to ideas</span>
            )}
          </div>
          <div className="text-xs text-stone-500 font-mono">
            Lv {m.level_index} {m.points_level} · <span className="text-amber-800 font-bold">{m.points_total} pts</span> · {m.checkins} check-ins · {m.habit_count} habit{m.habit_count === 1 ? "" : "s"}
          </div>
          {m.habits?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`member-habits-${m.id}`}>
              {m.habits.map((h) => (
                <span key={`${h.kind}-${h.title}`} className={`text-[11px] px-2 py-0.5 rounded-full ${h.kind === "stop" ? "bg-rose-50 text-rose-800" : "bg-emerald-50 text-emerald-800"}`}>{h.kind === "stop" ? "Stop: " : ""}{h.title}</span>
              ))}
            </div>
          )}
          <MemberChallenge c={m.challenge} id={m.id} />
        </div>
      </div>
      <div className="flex items-center gap-2">
        {!m.is_me && <WaterButton m={m} onWater={onWatered} gate={gate} />}
        <button data-testid={`open-wall-${m.id}`} onClick={openWall} title={gate.sharingOff && !m.is_me ? "Turn on sharing to cheer" : ""}
          className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full transition ${gate.sharingOff && !m.is_me ? "bg-stone-100 text-stone-400" : "bg-[#fdf3e3] text-amber-900 hover:bg-stone-900 hover:text-white"}`}>
          <MessageCircle className="w-3.5 h-3.5" /> {m.is_me ? "My wall" : "Cheer"}
        </button>
        {!m.is_me && m.accept_suggestions && (
          <button data-testid={`suggest-${m.id}`} onClick={openWall} className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full transition ${gate.sharingOff ? "bg-stone-100 text-stone-400" : "bg-amber-50 text-amber-900 hover:bg-amber-600 hover:text-white"}`}>
            <Lightbulb className="w-3.5 h-3.5" /> Suggest
          </button>
        )}
        <span className={`text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full ${LEVEL_COLORS[m.level]}`}>{m.level}</span>
      </div>
    </motion.li>
  );
}

export function MemberList({ members, canCheer, gate, onOpenWall, streak = 0, onWatered }) {
  if (members.length === 0) {
    return (
      <div className="warm-card p-10 text-center">
        <Sparkles className="w-8 h-8 mx-auto mb-2 text-amber-600" />
        <p className="text-stone-600">No one has joined this category yet. Be the first — turn on sharing above.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {canCheer && (
        <div className="flex items-center gap-2 text-xs text-sky-800 px-1" data-testid="watering-streak">
          <Droplets className="w-3.5 h-3.5" />
          {streak > 0 ? <span>Watering streak: <strong>{streak} day{streak === 1 ? "" : "s"}</strong>{streak < 5 && <span className="text-stone-500"> · {5 - streak} more for the Kind Gardener badge</span>}</span>
            : <span className="text-stone-500">Water a neighbour 5 days in a row to earn the Kind Gardener badge</span>}
        </div>
      )}
      <ul className="space-y-3">
        {members.map((m, i) => <MemberRow key={m.id} m={m} index={i} gate={gate} onOpenWall={onOpenWall} onWatered={onWatered} />)}
      </ul>
    </div>
  );
}

export function NotesInbox({ inbox, guidelines }) {
  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-7 warm-card p-5" data-testid="community-inbox">
        <div className="flex items-center gap-2 mb-3">
          <Inbox className="w-4 h-4 text-emerald-700" />
          <h3 className="font-display font-bold text-stone-900">Notes for you</h3>
        </div>
        {inbox.length === 0 ? (
          <p className="text-sm text-stone-500">No notes yet. Members in your categories can cheer you on here.</p>
        ) : (
          <ul className="space-y-2 max-h-48 overflow-y-auto">
            {inbox.slice(0, 10).map((p) => (
              <li key={p.id} className="text-sm border-l-2 border-emerald-300 pl-3" data-testid={`inbox-post-${p.id}`}>
                <span className={`text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1.5 ${p.kind === "support" ? "text-emerald-800" : "text-amber-800"}`}>
                  <HandleAvatar handle={p.from_name} size="sm" /> {p.kind} · {p.from_name} · {CATEGORY_META[p.category]?.label}{p.is_sample && <SampleTag id={`inbox-${p.id}`} />}
                </span>
                <p className="text-stone-800">{p.text}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="lg:col-span-5 warm-card p-5" data-testid="community-guidelines">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-amber-700" />
          <h3 className="font-display font-bold text-stone-900">Community guidelines</h3>
        </div>
        <ul className="space-y-1.5 text-xs text-stone-600 list-disc pl-4">
          {guidelines.map((g) => <li key={g}>{g}</li>)}
        </ul>
      </div>
    </div>
  );
}
